<template>
    <div class="admin-panel">
        <vue-good-table
            v-if="profile"
            ref="table"
            class="users-table"
            mode="remote"
            style-class="vgt-table bordered striped"
            :total-rows="totalCount"
            :pagination-options="paginationOptions"
            :select-options="selectOptions"
            :columns="columns"
            :rows="rows"
            @on-page-change="onPageChange"
            @on-sort-change="onSortChange"
            @on-column-filter="onColumnFilter"
            @on-per-page-change="onPerPageChange"
            @on-cell-click="onCellClick"
        >
            <template #selected-row-actions>
                <b-button class="button narrow-button button-danger" @click="onRemoveClick">
                    {{ $t('button.remove') }}
                </b-button>
            </template>
        </vue-good-table>

        <confirm-modal
            ref="modalConfirm"
            use-no-button
            :show-update-field="showUpdateField"
            :show-select-field="showSelectField"
            :select-options="modalSelectOptions"
            :default-value="defaultModalValue"
            :use-message="!showUpdateField && !showSelectField"
            :yes-button-text="showUpdateField || showSelectField ? $t('button.ok') : ''"
            :no-button-text="showUpdateField || showSelectField ? $t('button.cancel') : ''"
            rules="required|email|emailNotExists"
            @confirmed="updateUser"
            @rejected="resetConfirmModal"
        />

        <create-user @userCreated="appendUser" />
    </div>
</template>

<script>
    import 'vue-good-table/dist/vue-good-table.css';
    import { VueGoodTable } from 'vue-good-table';
    import api from '@/api';
    import ConfirmModal from './ConfirmModal';
    import { mapGetters } from 'vuex';
    import CreateUser from './CreateUser';

    export default {
        name: 'AdminPanel',

        components: {
            ConfirmModal,
            VueGoodTable,
            CreateUser
        },
        
        data() {
            return {
                rows: [],
                totalCount: 0,
                serverParams: {
                    columnFilters: {},
                    sortOptions: {},
                    page: 1,
                    perPage: 10
                },
                showUpdateField: false,
                showSelectField: false,
                defaultModalValue: '',
                modalSelectOptions: []
            };
        },

        computed: {
            ...mapGetters('user', [ 'profile' ]),
            
            ...mapGetters([ 'features' ]),
            
            columns() {
                return [
                    {
                        label: this.$t('label.name'),
                        field: 'name',
                        width: '150px',
                        filterOptions: {
                            enabled: true,
                            trigger: 'input + enter',
                            placeholder: this.$t('placeholder.search'),
                            filterDebounce: 500
                        }
                    },
                    {
                        label: this.$t('label.surname'),
                        field: 'surname',
                        width: '180px',
                        filterOptions: {
                            enabled: true,
                            trigger: 'input + enter',
                            placeholder: this.$t('placeholder.search'),
                            filterDebounce: 500
                        }
                    },
                    {
                        label: this.$t('label.email'),
                        field: 'mail',
                        width: '300px',
                        filterOptions: {
                            enabled: true,
                            trigger: 'input + enter',
                            placeholder: this.$t('placeholder.search'),
                            filterDebounce: 500
                        }
                    },
                    ...(this.features.vkAuth ? [ {
                        label: this.$t('label.vk'),
                        field: 'vkId',
                        width: '120px',
                        filterOptions: {
                            enabled: true,
                            trigger: 'input + enter',
                            placeholder: this.$t('placeholder.search'),
                            filterDebounce: 500
                        }
                    } ] : []),
                    {
                        label: this.$t('label.activated'),
                        field: 'activated',
                        tdClass: 'clickable-td',
                        filterOptions: {
                            enabled: true,
                            trigger: 'enter',
                            placeholder: this.$t('placeholder.all'),
                            filterDropdownItems: [ true, false ]
                        }
                    },
                    {
                        label: this.$t('label.lastLogin'),
                        field: 'lastLogin',
                        tdClass: 'clickable-td',
                        type: 'date',
                        filterable: true,
                        formatFn: value => {
                            if (!value)
                                return this.$t('label.noLogin');
                            
                            return new Date(value).toLocaleDateString('ru-RU');
                        },
                        filterOptions: {
                            enabled: true,
                            trigger: 'enter',
                            placeholder: this.$t('placeholder.all'),
                            filterDropdownItems: [
                                { value: 24 * 60 * 60 * 1000, text: this.$t('label.day') },
                                { value: 7 * 24 * 60 * 60 * 1000, text: this.$t('label.week') },
                                { value: 30 * 24 * 60 * 60 * 1000, text: this.$t('label.month') },
                                { value: 182 * 24 * 60 * 60 * 1000, text: this.$t('label.halfyear') },
                                { value: 365 * 24 * 60 * 60 * 1000, text: this.$t('label.year') }
                            ]
                        }
                    },
                    {
                        label: this.$t('label.mirera'),
                        field: 'permissions.mirera',
                        tdClass: 'clickable-td',
                        filterOptions: {
                            enabled: true,
                            trigger: 'enter',
                            placeholder: this.$t('placeholder.all'),
                            filterDropdownItems: [ 'teacher', 'user' ]
                        }
                    },
                    {
                        label: this.$t('label.piktomir'),
                        field: 'permissions.pm',
                        tdClass: 'clickable-td',
                        filterOptions: {
                            enabled: true,
                            trigger: 'enter',
                            placeholder: this.$t('placeholder.all'),
                            filterDropdownItems: [ 'teacher', 'user' ]
                        }
                    }
                ];
            },

            paginationOptions() {
                return {
                    enabled: true,
                    dropdownAllowAll: false,
                    jumpFirstOrLast: true,
                    nextLabel: this.$t('label.next'),
                    prevLabel: this.$t('label.prev'),
                    firstLabel: this.$t('label.first'),
                    lastLabel: this.$t('label.last'),
                    rowsPerPageLabel: this.$t('label.rowsPerPage'),
                    ofLabel: this.$t('label.of')
                };
            },

            selectOptions() {
                return {
                    enabled: true,
                    selectOnCheckboxOnly: true,
                    selectionText: this.$t('label.rowsSelected'),
                    clearSelectionText: this.$t('label.clear'),
                    disableSelectInfo: false
                };
            }
        },

        created() {
            this.loadItems();
        },

        methods: {
            async appendUser() {
                this.serverParams.page = Math.ceil(Math.max(1, this.totalCount) / this.serverParams.perPage);

                await this.onPageChange({ currentPage: this.serverParams.page });
            },

            updateParams(newProps) {
                this.serverParams = { ...this.serverParams, ...newProps };
            },

            async onPageChange(params) {
                this.updateParams({ page: params.currentPage });
                await this.loadItems();
            },

            async onPerPageChange(params) {
                this.updateParams({ perPage: params.currentPerPage });
                await this.loadItems();
            },

            async onSortChange(params) {
                let sortOptions = {};

                for (let sortParam of params) {
                    if (sortParam.type === 'none')
                        continue;

                    sortOptions[sortParam.field] = sortParam.type === 'asc' ? 1 : -1;
                }

                this.updateParams({ sortOptions });
                await this.loadItems();
            },

            async onColumnFilter(params) {
                let columnFilters = {};

                for (let filter in params.columnFilters) {
                    if (params.columnFilters[filter])
                        columnFilters[filter] = params.columnFilters[filter];
                }

                this.updateParams({ columnFilters });
                await this.loadItems();
            },

            onCellClick(params) {
                if (window.getSelection().toString())
                    return;

                switch (params.column.field) {
                    case 'activated':
                    case 'permissions.mirera':
                    case 'permissions.pm':
                        this.$nextTick(() => {
                            this.$refs.modalConfirm.showModal(params);
                        });
                        break;

                    case 'mail':
                        this.showUpdateField = true;

                        this.$nextTick(() => {
                            this.$refs.modalConfirm.showModal(params);
                        });
                        break;
                    
                    default:
                        return;
                }
            },

            onRemoveClick() {
                this.$nextTick(() => {
                    this.$refs.modalConfirm.showModal({
                        column: { field: 'status' },
                        selectedRows: this.$refs.table.selectedRows
                    });
                });
            },

            async updateUser(params) {
                this.resetConfirmModal();

                switch (params.column.field) {
                    case 'mail': {
                        await api.post(`/app/admin/users/${params.row._id}/changeMail`, { newMail: params.updateValue });
                        await this.loadItems();
                        
                        break;
                    }

                    case 'activated': {
                        await api.post(`/app/admin/users/${params.row._id}/${params.row.activated ? 'deactivate' : 'activate'}`);
                        await this.loadItems();
                        
                        break;
                    }

                    case 'permissions.mirera': {
                        let mireraAction = params.row.permissions.mirera === this.$defines.SERVICE_ACCOUNT_TYPES.USER
                            ? 'approve' : 'deprive';
                        
                        await api.post(`/app/admin/users/${params.row._id}/permissions/mirera/${mireraAction}`);
                        await this.loadItems();
                        
                        break;
                    }

                    case 'permissions.pm': {
                        let pmAction = params.row.permissions.pm === this.$defines.SERVICE_ACCOUNT_TYPES.USER
                            ? 'approve' : 'deprive';
                        
                        await api.post(`/app/admin/users/${params.row._id}/permissions/pm/${pmAction}`);
                        await this.loadItems();
                        
                        break;
                    }

                    case 'status': {
                        await api.post('app/admin/users/delete', {
                            userIds: params.selectedRows.map(u => u._id)
                        });
                        await this.loadItems();
                        
                        break;
                    }
                    
                    default: {
                        return;
                    }
                }
            },

            async loadItems() {
                let response = await api.post('/app/admin/users', this.serverParams);

                if (response && response.data) {
                    this.totalCount = response.data.totalCount;
                    this.rows = response.data.users;
                    this.serverParams.page = response.data.page;
                }
            },
            
            resetConfirmModal() {
                this.showUpdateField = false;
                this.showSelectField = false;
                this.defaultModalValue = '';
                this.modalSelectOptions = [];
            }
        }
    };
</script>

<style scoped>
    .admin-panel {
        margin-left: -80px;
        margin-right: -80px;
    }

    /deep/ label {
        margin-bottom: 0;
    }

    .users-table {
        margin-bottom: 10px;
        border-radius: 5px;
    }

    /deep/ .clickable-td {
        cursor: pointer;
    }

    .narrow-button {
        height: 19px;
        width: 100px;
        font-weight: 300;
        display: flex !important;
        justify-content: center !important;
        align-items: center !important;
    }
</style>
